import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardHeader } from "@/components/DashboardHeader";
import { LokationerAdmin } from "@/components/LokationerAdmin";
import { Shield, X, Check, UserMinus, Pencil, Loader2 } from "lucide-react";
import RettighedsEditor from "@/components/RettighedsEditor";
import ModalPortal from "@/components/ModalPortal";
import { rettighederTilDict } from "@/lib/rettigheder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_BADGE = {
  owner:      "bg-violet-100 text-violet-700 border border-violet-300 dark:bg-violet-500/10 dark:text-violet-400 dark:border-violet-500/20",
  superadmin: "bg-violet-100 text-violet-700 border border-violet-300 dark:bg-violet-500/10 dark:text-violet-400 dark:border-violet-500/20",
  // API admin — skrivebeskyttet super-rolle. Slate, så den skiller sig fra superadmin.
  api_admin:  "bg-slate-200 text-slate-700 border border-slate-300 dark:bg-slate-500/10 dark:text-slate-300 dark:border-slate-500/20",
  admin:      "bg-emerald-100 text-emerald-700 border border-emerald-300 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20",
  member:     "bg-blue-100 text-blue-700 border border-blue-300 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20",
  // #38.6: Medarbejder (light) — teal, så den skiller sig fra fuld medarbejder.
  light:      "bg-teal-100 text-teal-700 border border-teal-300 dark:bg-teal-500/10 dark:text-teal-300 dark:border-teal-500/20",
  guest:      "bg-amber-100 text-amber-700 border border-amber-300 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20",
};

const ROLE_LABEL = { owner: "SuperAdmin", superadmin: "SuperAdmin", api_admin: "API admin", admin: "Admin", member: "Medarbejder", light: "Medarbejder (light)", guest: "Elev" };
const DISPLAY_NAMES = { "Koebenhavn": "København", "Koebenhavn-frisor": "København Frisør" };
const displayName = (name) => name ? (DISPLAY_NAMES[name] || name) : "—";
const displayAfdeling = (u) => u.primaer_afdeling || (u.city ? displayName(u.city) : "—");

// Render primær-afdeling-cellen: viser "Ikke koblet til Podio"-badge for ansatte
// uden kobling, ellers afdelingsnavnet. Bruges af både desktop-tabel og mobil-kort.
function renderAfdeling(u) {
  const afd = displayAfdeling(u);
  const erAnsat = u.role === "member" || u.role === "admin" || u.role === "light";
  const ikkeKoblet = erAnsat && afd === "—" && !u.medarbejder_item_id;
  if (ikkeKoblet) {
    return (
      <span
        title="Login-emailen findes ikke på nogen Podio Medarbejdere-record. Tilføj emailen til medarbejderen i Podio og kør sync igen."
        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-amber-600 dark:text-amber-400"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
        Ikke koblet til Podio
      </span>
    );
  }
  return afd;
}

function RoleBadge({ role }) {
  return (
    <span className={`text-[13px] px-2 py-0.5 rounded-full ${ROLE_BADGE[role] || "text-muted-foreground"}`}>
      {ROLE_LABEL[role] || role || "—"}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("da-DK", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// ─── Rettigheds-modal ──────────────────────────────────────────────────────
// Vises når superadmin klikker "Rediger rettigheder" på en bruger.
// Indeholder rolle-radio + toggleable rettigheder for Medarbejdere
// (disabled checkmarks for Admin, ingen liste for Superadmin).
function RettighedsModal({ user: targetUser, currentRole, token, onClose, onSaved }) {
  const [rolle, setRolle] = useState(currentRole || "member");
  const [rettigheder, setRettigheder] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [medarbejderItemId, setMedarbejderItemId] = useState(targetUser?.medarbejder_item_id || null);
  const [koblingMsg, setKoblingMsg] = useState(null);

  useEffect(() => {
    let abort = false;
    // Hent eksisterende rettigheder via medarbejder-stamdata hvis kobling findes,
    // ellers via bruger-fallback (user_rettigheder).
    const profP = medarbejderItemId
      ? axios.get(`${API}/admin/medarbejdere/${medarbejderItemId}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null)
      : Promise.resolve(null);
    profP.then((profRes) => {
      if (abort) return;
      const r = profRes?.data?.profil?.rettigheder;
      setRettigheder(rettighederTilDict(r));
    }).finally(() => !abort && setLoading(false));
    return () => { abort = true; };
  }, [targetUser, token, medarbejderItemId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // 1. Skift rolle hvis ændret (member ↔ admin)
      if (rolle !== currentRole && (rolle === "member" || rolle === "admin")) {
        await axios.put(`${API}/admin/users/${encodeURIComponent(targetUser.email)}/role`, { role: rolle }, { headers: { Authorization: `Bearer ${token}` } });
      }
      // 2. Skift super-user-niveau (superadmin / api_admin). Kun owner kan tildele —
      //    backend afviser ellers med 403. Skift væk fra et super-niveau → fjern.
      const SUPER = ["superadmin", "api_admin"];
      const erSuperNu = ["superadmin", "owner", "api_admin"].includes(currentRole);
      if (SUPER.includes(rolle) && rolle !== currentRole) {
        // owner forbliver owner (skal ikke nedgraderes til superadmin) — sættes ikke her.
        if (currentRole !== "owner") {
          await axios.put(`${API}/admin/super-users/${encodeURIComponent(targetUser.email)}`, { level: rolle }, { headers: { Authorization: `Bearer ${token}` } });
        }
      } else if (!SUPER.includes(rolle) && rolle !== "owner" && erSuperNu && currentRole !== "owner") {
        await axios.put(`${API}/admin/super-users/${encodeURIComponent(targetUser.email)}`, { level: null }, { headers: { Authorization: `Bearer ${token}` } });
      }

      // 3. Gem individuelle rettigheder — kun relevant for medarbejder-rollen.
      // Backend ignorerer dem for admin/superadmin (de har baseline/fuld adgang).
      const erMedarbejder = rolle === "member";
      if (erMedarbejder) {
        let savedItemId = medarbejderItemId;
        // Forsøg auto-kobling hvis vi ikke har et item_id endnu.
        if (!savedItemId) {
          try {
            const koblRes = await axios.post(
              `${API}/admin/users/auto-kobl`,
              { email: targetUser.email },
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (koblRes.data?.koblet && koblRes.data?.medarbejder_item_id) {
              savedItemId = koblRes.data.medarbejder_item_id;
              setMedarbejderItemId(savedItemId);
              setKoblingMsg(`Automatisk koblet til ${koblRes.data.medarbejder_navn || "medarbejder"}`);
            }
          } catch { /* fortsæt med fallback nedenfor */ }
        }
        if (savedItemId) {
          await axios.put(`${API}/admin/medarbejdere/${savedItemId}/rettigheder`, { rettigheder }, { headers: { Authorization: `Bearer ${token}` } });
        } else {
          // Fallback: gem direkte i user_rettigheder via email-baseret endpoint.
          await axios.put(`${API}/admin/users/${encodeURIComponent(targetUser.email)}/rettigheder`, { rettigheder }, { headers: { Authorization: `Bearer ${token}` } });
        }
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      alert(e?.response?.data?.detail || "Kunne ikke gemme rettigheder");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalPortal onClose={onClose}>
      <div className="bg-background border border-border rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border">
          <h2 className="text-base font-bold flex items-center gap-2 min-w-0">
            <Shield className="w-4 h-4 text-[#FF2A6D] shrink-0" />
            <span className="break-words min-w-0">Rediger rettigheder for {targetUser?.name || targetUser?.email}</span>
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-secondary shrink-0"><X className="w-4 h-4" /></button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Henter...</div>
        ) : (
          <div className="p-5 space-y-5">
            <div>
              <p className="text-[13px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">Rolle</p>
              <div className="flex flex-col gap-2">
                {[
                  { val: "member", label: "Medarbejder", desc: "Kun egen forside og eget stamkort. Rettigheder tildeles individuelt nedenfor." },
                  { val: "admin", label: "Admin", desc: "KPI, Timer, Vagtplan, Elever, Medarbejdere (ikke-fortrolige felter), godkend kalender." },
                  { val: "superadmin", label: "SuperAdmin", desc: "Fuld adgang — ingen begrænsninger." },
                  { val: "api_admin", label: "API admin", desc: "Samme læseadgang som SuperAdmin, men skrivebeskyttet — kan ikke ændre noget. Kun owner kan tildele." },
                ].map(r => (
                  <label key={r.val} className="flex items-start gap-2.5 cursor-pointer rounded-lg p-2 hover:bg-secondary/50">
                    <input type="radio" name="rolle" checked={rolle === r.val} onChange={() => setRolle(r.val)}
                      className="mt-1 accent-[#FF2A6D]" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{r.label}</p>
                      <p className="text-[13px] text-muted-foreground leading-snug">{r.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <RettighedsEditor rolle={rolle} vaerdier={rettigheder} onChange={setRettigheder} token={token} brugerNavn={targetUser?.name || targetUser?.email || ""} />

            {koblingMsg && (
              <div className="text-[13px] bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-lg p-2.5 flex items-center gap-2">
                <Check className="w-3.5 h-3.5" />
                {koblingMsg}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border bg-secondary/30">
          <button onClick={onClose} className="px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-secondary transition-colors">Annullér</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-1.5 rounded-lg text-sm font-semibold bg-[#FF2A6D] text-white hover:bg-[#e02560] transition-colors disabled:opacity-60">
            {saving ? "Gemmer..." : "Gem"}
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}

// ─── Ret rolle-dialog (#61 DEL A) — kun SuperAdmin ───────────────────────────
// Sætter medarbejderens rolle direkte via medarbejder-stamdata (PUT .../rolle).
// Kræver et medarbejder item_id (rows uden kobling kan ikke bruge den).
const RET_ROLLE_VALG = [
  { val: "member", label: "Medarbejder" },
  { val: "light", label: "Medarbejder (light)" },
  { val: "admin", label: "Admin" },
  { val: "superadmin", label: "SuperAdmin" },
  { val: "api_admin", label: "API admin (skrivebeskyttet)" },
];

function RetRolleDialog({ user: targetUser, itemId, currentRole, token, onClose, onSaved }) {
  const [rolle, setRolle] = useState(
    RET_ROLLE_VALG.some(r => r.val === currentRole) ? currentRole : "member"
  );
  const [saving, setSaving] = useState(false);
  const [fejl, setFejl] = useState(null);

  const handleSave = async () => {
    setFejl(null);
    setSaving(true);
    try {
      await axios.put(`${API}/admin/medarbejdere/${itemId}/rolle`, { rolle },
        { headers: { Authorization: `Bearer ${token}` } });
      onSaved && (await onSaved());
      onClose();
    } catch (e) {
      setFejl(e?.response?.data?.detail || "Kunne ikke ændre rolle");
      setSaving(false);
    }
  };

  return (
    <ModalPortal onClose={onClose}>
      <div className="bg-background border border-border rounded-2xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border">
          <h2 className="text-base font-bold flex items-center gap-2 min-w-0">
            <Shield className="w-4 h-4 text-[#FF2A6D] shrink-0" />
            <span className="break-words min-w-0">Ret rolle for {targetUser?.name || targetUser?.email}</span>
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-secondary shrink-0"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-2">
          {RET_ROLLE_VALG.map(r => (
            <label key={r.val} className="flex items-center gap-2.5 cursor-pointer rounded-lg p-2 hover:bg-secondary/50">
              <input type="radio" name="ret-rolle" checked={rolle === r.val} onChange={() => setRolle(r.val)}
                className="accent-[#FF2A6D]" />
              <span className="text-sm font-medium">{r.label}</span>
            </label>
          ))}
          {fejl && (
            <p className="text-[13px] text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{fejl}</p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border bg-secondary/30">
          <button onClick={onClose} className="px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-secondary transition-colors">Annullér</button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold bg-[#FF2A6D] text-white hover:bg-[#e02560] transition-colors disabled:opacity-60">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {saving ? "Gemmer..." : "Gem"}
          </button>
        </div>
      </div>
    </ModalPortal>
  );
}


export default function UsersPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null); // {processed,total} mens jobbet kører
  const [podioStatus, setPodioStatus] = useState(null);
  const [superUsers, setSuperUsers] = useState([]);
  const [superLevels, setSuperLevels] = useState({}); // email(lower) → "superadmin"|"owner"|"api_admin"
  const [promotingEmail, setPromotingEmail] = useState(null);
  const [rettighedsTarget, setRettighedsTarget] = useState(null); // valgt bruger til rettigheds-modal
  // #61 DEL A: Ret rolle-dialog + "Fjern fra medarbejdere" (kun SuperAdmin).
  const [retRolleTarget, setRetRolleTarget] = useState(null); // valgt bruger til Ret rolle
  const [fjernerEmail, setFjernerEmail] = useState(null);     // email der fjernes lige nu
  // #39.1: Brugere-listen er ren read-only oversigt med søgefelt (navn/email).
  // Al rolle-/rettigheds-administration sker under Medarbejdere.
  const [search, setSearch] = useState("");

  const authHeaders = { Authorization: `Bearer ${token}` };
  const isAdmin = ["admin", "superadmin", "owner", "api_admin"].includes(user?.role);
  const isOwner = user?.role === "owner";
  const isSuperAdmin = ["superadmin", "owner", "api_admin"].includes(user?.role);

  function loadUsers() {
    return axios.get(`${API}/admin/users`, { headers: authHeaders }).then(r => setUsers(r.data));
  }

  function loadSuperUsers() {
    if (!isSuperAdmin) return Promise.resolve();
    return axios.get(`${API}/admin/super-users`, { headers: authHeaders })
      .then(r => {
        const docs = r.data.super_users || [];
        setSuperUsers(docs.map(s => s.email));
        // Bevar niveau (superadmin/owner/api_admin) pr. email, så api_admin vises korrekt.
        setSuperLevels(Object.fromEntries(docs.map(s => [(s.email || "").toLowerCase(), s.level])));
      })
      .catch(() => {});
  }

  useEffect(() => {
    if (!isAdmin) {
      navigate("/", { replace: true });
      return;
    }
    Promise.all([
      loadUsers(),
      axios.get(`${API}/podio/status`).then(r => setPodioStatus(r.data)),
      loadSuperUsers(),
    ]).catch(err => {
      if (err.response?.status === 401) logout();
    }).finally(() => setLoading(false));
  }, [user, token]); // eslint-disable-line react-hooks/exhaustive-deps

  async function syncRoles() {
    // Sync er et BAGGRUNDSJOB på backend (en fuld kørsel tager 1-2 min og ville
    // ellers ramme nginx' proxy-timeout). Vi starter jobbet og poller status.
    setSyncing(true);
    setSyncProgress(null);
    try {
      const start = await axios.post(`${API}/admin/users/sync-roles`, {}, { headers: authHeaders });
      if (start.data?.status === "already_running") {
        // Et job kører allerede — vi hopper bare ind i poll-loopet.
      }
      // Poll status indtil done/error (eller timeout efter ~5 min).
      const deadline = Date.now() + 5 * 60 * 1000;
      let job = null;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise(r => setTimeout(r, 2000));
        try {
          const s = await axios.get(`${API}/admin/users/sync-roles/status`, { headers: authHeaders });
          job = s.data;
          if (job?.total) setSyncProgress({ processed: job.processed || 0, total: job.total });
        } catch { /* forbigående netværksfejl — prøv igen */ }
        if (job && (job.status === "done" || job.status === "error")) break;
        if (Date.now() > deadline) break;
      }
      await loadUsers();
      if (job?.status === "done") {
        alert(`Synkronisering færdig.\nOpdateret: ${job.updated} brugere, sprunget over: ${job.skipped}.`);
      } else if (job?.status === "error") {
        alert(`Synkronisering fejlede: ${job.error || "ukendt fejl"}`);
      } else {
        alert("Synkronisering kører stadig i baggrunden — listen er genindlæst med de hidtidige opdateringer.");
      }
    } catch {
      alert("Kunne ikke starte synkronisering");
    } finally {
      setSyncing(false);
      setSyncProgress(null);
    }
  }

  async function handleRoleChange(email, currentRole) {
    const newRole = currentRole === "admin" ? "member" : "admin";
    const msg = newRole === "admin"
      ? `Løft ${email} til Admin?`
      : `Nedgradér ${email} til Medarbejder?`;
    if (!window.confirm(msg)) return;
    setPromotingEmail(email);
    try {
      await axios.put(`${API}/admin/users/${encodeURIComponent(email)}/role`, { role: newRole }, { headers: authHeaders });
      await loadUsers();
    } catch (err) {
      alert(err.response?.data?.detail || "Fejl");
    } finally {
      setPromotingEmail(null);
    }
  }

  // #38.6: sæt rolle direkte (member / light / admin) via dropdown.
  async function setRole(email, newRole) {
    setPromotingEmail(email);
    try {
      await axios.put(`${API}/admin/users/${encodeURIComponent(email)}/role`, { role: newRole }, { headers: authHeaders });
      await loadUsers();
    } catch (err) {
      alert(err.response?.data?.detail || "Fejl ved ændring af rolle");
    } finally {
      setPromotingEmail(null);
    }
  }

  async function handlePromote(email, currentLevel) {
    const newLevel = currentLevel ? null : "superadmin";
    const msg = newLevel
      ? `Giv ${email} SuperAdmin-rettigheder?`
      : `Fjern SuperAdmin-rettigheder fra ${email}?`;
    if (!window.confirm(msg)) return;
    setPromotingEmail(email);
    try {
      await axios.put(`${API}/admin/super-users/${encodeURIComponent(email)}`, { level: newLevel }, { headers: authHeaders });
      await Promise.all([loadUsers(), loadSuperUsers()]);
    } catch (err) {
      alert(err.response?.data?.detail || "Fejl");
    } finally {
      setPromotingEmail(null);
    }
  }

  // #61 DEL A: Sæt en (fejlkoblet) person tilbage til Elev. Kræver et
  // medarbejder item_id — "Ikke koblet til Podio"-rækker har ingen stamdata at
  // fjerne, så knappen vises ikke for dem.
  async function handleFjernFraMedarbejdere(u) {
    if (!u.medarbejder_item_id) return;
    const navn = u.name || u.email;
    if (!window.confirm(`Sæt ${navn} tilbage til Elev? Personen fjernes fra medarbejdere.`)) return;
    setFjernerEmail(u.email);
    try {
      await axios.post(`${API}/admin/medarbejdere/${u.medarbejder_item_id}/fjern-fra-medarbejdere`, {}, { headers: authHeaders });
      await loadUsers();
    } catch (err) {
      alert(err.response?.data?.detail || "Kunne ikke fjerne personen fra medarbejdere");
    } finally {
      setFjernerEmail(null);
    }
  }

  const enrichedUsers = users.map(u => {
    const email = (u.email || "").toLowerCase();
    if (email === "soren@beautyandstyle.dk") return { ...u, role: "owner" };
    if (superUsers.includes(email)) return { ...u, role: superLevels[email] || "superadmin" };
    return u;
  }).filter(u => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader
        refreshing={false}
        onRefresh={() => {}}
        podioStatus={podioStatus}
        lastUpdated={null}
        user={user}
        onLogout={logout}
      />
      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="mt-6 mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight mb-1">Brugere</h1>
            <p className="text-[13px] text-muted-foreground">
              Oversigt over alle der har logget ind, med rolle og afdeling.
              {isAdmin && <> Roller og rettigheder administreres under <span className="font-semibold">Medarbejdere</span>.</>}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Søg navn eller email…"
              className="bg-secondary border border-border rounded-lg px-3 h-9 text-sm w-full sm:w-56 focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]"
            />
            {isSuperAdmin && (
              <button
                onClick={syncRoles}
                disabled={syncing}
                className="hidden sm:inline-flex items-center px-4 h-9 text-[13px] rounded-lg border border-border hover:bg-secondary transition-colors disabled:opacity-50 shrink-0"
                title="Genvalidér alle brugeres roller mod Podio"
              >
                {syncing
                  ? (syncProgress?.total ? `Opdaterer… ${syncProgress.processed}/${syncProgress.total}` : "Opdaterer…")
                  : "Synk roller fra Podio"}
              </button>
            )}
          </div>
        </div>

        {/* Desktop: tabel-layout (uændret). Skjult på mobil. */}
        <div className="hidden sm:block rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/50">
                <th className="text-left px-4 py-3 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Navn</th>
                <th className="text-left px-4 py-3 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Email</th>
                <th className="text-left px-4 py-3 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Rolle</th>
                <th className="text-left px-4 py-3 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Primær afdeling</th>
                <th className="text-left px-4 py-3 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Seneste login</th>
                {isSuperAdmin && (
                  <th className="text-right px-4 py-3 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Handlinger</th>
                )}
              </tr>
            </thead>
            <tbody>
              {enrichedUsers.map((u, i) => (
                <tr key={u.user_id || i} className="border-b border-border hover:bg-secondary/50 transition-colors">
                  <td className="px-4 py-3 text-foreground font-medium">{u.name || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.email || "—"}</td>
                  <td className="px-4 py-3">
                    <RoleBadge role={u.role} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {renderAfdeling(u)}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{formatDate(u.updated_at)}</td>
                  {isSuperAdmin && (
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {u.medarbejder_item_id ? (
                          <>
                            <button onClick={() => setRetRolleTarget(u)}
                              title="Ret rolle"
                              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[13px] font-medium border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors whitespace-nowrap">
                              <Pencil className="w-3.5 h-3.5" /> Ret rolle
                            </button>
                            <button onClick={() => handleFjernFraMedarbejdere(u)}
                              disabled={fjernerEmail === u.email}
                              title="Sæt tilbage til Elev — fjern fra medarbejdere"
                              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[13px] font-medium border border-red-500/30 text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-50 whitespace-nowrap">
                              {fjernerEmail === u.email ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserMinus className="w-3.5 h-3.5" />}
                              Fjern
                            </button>
                          </>
                        ) : (
                          <span className="text-[12px] text-muted-foreground italic">Ingen Podio-kobling</span>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {enrichedUsers.length === 0 && (
                <tr>
                  <td colSpan={isSuperAdmin ? 6 : 5} className="px-4 py-8 text-center text-muted-foreground">
                    {search ? "Ingen brugere matcher søgningen" : "Ingen brugere fundet"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobil: kort-layout. Skjult fra sm: og op. */}
        <div className="sm:hidden space-y-3">
          {enrichedUsers.map((u, i) => (
            <div key={u.user_id || i} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-3">
                <p className="font-semibold text-foreground break-words min-w-0">{u.name || "—"}</p>
                <div className="shrink-0">
                  <RoleBadge role={u.role} />
                </div>
              </div>
              <p className="text-[13px] text-muted-foreground break-all mt-0.5">{u.email || "—"}</p>
              <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-[13px]">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Primær afdeling</p>
                  <p className="text-foreground break-words mt-0.5">{renderAfdeling(u)}</p>
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Seneste login</p>
                  <p className="text-foreground break-words mt-0.5">{formatDate(u.updated_at)}</p>
                </div>
              </div>
              {isSuperAdmin && u.medarbejder_item_id && (
                <div className="mt-3 pt-3 border-t border-border flex items-center gap-2">
                  <button onClick={() => setRetRolleTarget(u)}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[13px] font-medium border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
                    <Pencil className="w-3.5 h-3.5" /> Ret rolle
                  </button>
                  <button onClick={() => handleFjernFraMedarbejdere(u)} disabled={fjernerEmail === u.email}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[13px] font-medium border border-red-500/30 text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-50">
                    {fjernerEmail === u.email ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserMinus className="w-3.5 h-3.5" />}
                    Fjern fra medarbejdere
                  </button>
                </div>
              )}
            </div>
          ))}
          {enrichedUsers.length === 0 && (
            <div className="rounded-xl border border-border px-4 py-8 text-center text-muted-foreground text-sm">
              {search ? "Ingen brugere matcher søgningen" : "Ingen brugere fundet"}
            </div>
          )}
        </div>

        {/* #61 DEL A: Ret rolle-dialog (kun SuperAdmin, kun rækker med kobling). */}
        {retRolleTarget && (
          <RetRolleDialog
            user={retRolleTarget}
            itemId={retRolleTarget.medarbejder_item_id}
            currentRole={retRolleTarget.role}
            token={token}
            onClose={() => setRetRolleTarget(null)}
            onSaved={loadUsers}
          />
        )}
      </main>
    </div>
  );
}
